/**
 * **The cuts a trainer reads a wellbeing number by**, and the one place they are written.
 *
 * Owner, 16 September 2026: *"zmien nastroj zeby byl czerwony tylko ponizej 5.0 i sen by byl
 * czerwony ponizej 6.5h"*, then, later the same day: *"Energia powyzej 8.0 zielona ma byc ponizej 6.0
 * czerwona"*. The two signals carry **their own low cut**, because the shared one was firing almost
 * permanently: most people sleep under seven hours, so a seven-hour cut painted the sleep line red
 * on most of the roster most of the time, and a colour that is always on is not a signal. 6.5 h
 * marks a genuinely short night; 6.0 on the contract's 1-10 rating marks a genuinely bad week.
 *
 * Mood's low cut was 5.0 for part of 16 September and is **6.0** — the owner's second reading of the
 * same scale, and the only one recorded here.
 *
 * This is the split the previous revision of this file predicted in as many words: the 2 September
 * rule ("czerwony ponizej sredniej 7h … i energia ponizej 7 tez na czerwono") put both signals on
 * one pair of numbers and noted that they coincided rather than derived, "so a future change to one
 * of them splits this table rather than editing a shared constant". That is what happened, and the
 * table is split rather than re-tuned.
 *
 * **SLEEP HAS NO HIGH BAND**, and that is the second instruction of the same day: *"a sen dalej
 * uzywajmy do snu niebieskiego koloru gdy jest git a od progu 6.5 na czerwono go zaznaczajmy
 * (wszystko powyzej 6.5 niebieskie, ponizej czerwone)"*. Two bands, not three. It supersedes the
 * 2 September "zielony powzyej 8" for sleep and the grey neutral this file carried for it earlier
 * the same day; the OK band is the column's own blue again.
 *
 * The reasoning holds on its own: a long night is not an achievement to celebrate the way a good
 * adherence figure is, and eight hours is the target rather than the exceptional case, so a green
 * band there was rewarding the normal. Mood keeps its three, because a mood of nine IS worth
 * seeing.
 *
 * It lives in `entities/client` because two surfaces read it and neither owns it: the roster's SEN
 * and NASTROJ columns, and the client detail's two Trendy charts. Both therefore moved together,
 * which is the point of the module: the same client cannot be "short sleep" on one screen and
 * unremarkable on the other.
 *
 * **THE LOW CUT IS THE TRAINER'S OWN LEVEL** (owner, 2 October 2026; attention-queue R25). A
 * trainer may move "low sleep" and "low energy" on `/zadania/rodzaje`, and a column that still
 * painted red under 6.5 h while their task fired under 6 would be two verdicts about one night. So
 * every function here takes the levels in force — `model/wellbeingLevels` reads them off the
 * preferences, `lib/useWellbeingLevels` is the one hook. The levels are a REQUIRED argument: a
 * caller that forgot them used to paint by the fixed numbers below without saying so. Those numbers
 * are now only what `wellbeingLevels` falls back to when neither read can say. Mood's HIGH cut is
 * not a level and stays the owner's 8.
 *
 * **What this agrees with, and what it does not.** The LEVEL is the one the trainer's low-sleep and
 * low-energy tasks are judged against, so a band and a task cannot disagree about where low begins.
 * They are not the same verdict: a band here colours ONE reading — on the roster, the newest — while
 * the task is raised on the server's AVERAGE of the client's recent readings (R22). A client can
 * have a red newest night and no task, or a task and a blue newest night.
 *
 * Pure: no React, no DOM, no i18next. The band is named for what it MEANS; every colour token that
 * draws it belongs to a stylesheet.
 */

/** Which of the two signals a reading belongs to. They no longer share their low cut. */
export type WellbeingSignal = 'sleep' | 'mood';

/** How a reading reads against its signal's two cuts. Never a colour name — see the header. */
export type WellbeingBand = 'low' | 'middling' | 'high';

interface WellbeingCuts {
  /** Under this, the reading is low. Strictly under: the cut itself is not. */
  readonly lowBelow: number;
  /**
   * Over this, it is high. Strictly over: the cut itself is not.
   *
   * `null` means the signal has NO high band — everything that is not low is simply fine. Sleep is
   * that signal: owner, 16 September 2026, *"wszystko powyzej 6.5 niebieskie, ponizej czerwone"*.
   */
  readonly highAbove: number | null;
}

/**
 * The cuts, per signal.
 *
 * Sleep is in HOURS and mood is on the contract's 1-10 rating (`TrainingMoodReading.Value`), so the
 * two sets of numbers are not comparable with each other and must never be folded back into one
 * constant because they happen to share a ceiling.
 */
export const WELLBEING_CUTS: Readonly<Record<WellbeingSignal, WellbeingCuts>> = {
  sleep: { lowBelow: 6.5, highAbove: null },
  mood: { lowBelow: 6, highAbove: 8 },
};

/** Under what a reading of each signal is low: the trainer's level for it. */
export type WellbeingLevels = Readonly<Record<WellbeingSignal, number>>;

/** The levels where neither read can say — `wellbeingLevels`' last resort. See the header. */
export const DEFAULT_WELLBEING_LEVELS: WellbeingLevels = {
  sleep: WELLBEING_CUTS.sleep.lowBelow,
  mood: WELLBEING_CUTS.mood.lowBelow,
};

/**
 * One signal's two cuts under a level.
 *
 * Energy's level may be set as high as 9, past the fixed high cut of 8. A reading cannot be both
 * low and high, and low is the verdict the trainer asked for, so the high band then starts at the
 * level: nothing between the two is left, and nothing is in both.
 */
function cutsFor(signal: WellbeingSignal, levels: WellbeingLevels): WellbeingCuts {
  const lowBelow = levels[signal];
  const { highAbove } = WELLBEING_CUTS[signal];
  return { lowBelow, highAbove: highAbove === null ? null : Math.max(highAbove, lowBelow) };
}

/**
 * One band, as the extent of the values it covers.
 *
 * The bounds are what a **drawing** needs — two horizontal lines across a plot — and at a bound the
 * two neighbouring spans meet on one value, which is why `wellbeingBand` and not these bounds is
 * what decides where a reading itself falls. A span pair says where the colour changes; the
 * predicate says which side a number is on. Conflating the two is how a reading of exactly 6.5
 * ends up drawn half red.
 */
export interface WellbeingBandSpan {
  readonly band: WellbeingBand;
  /** The lowest value the span covers. `-Infinity` on the bottom span: nobody sleeps a floor. */
  readonly from: number;
  /** The highest. `Infinity` on the top span. */
  readonly to: number;
}

/**
 * One signal's three spans, ascending. Contiguous and total: every real number is inside exactly
 * one.
 *
 * A function rather than a constant, because the spans are now a property of the signal. Callers
 * that drew both columns from one exported array are what this signature exists to break.
 */
export function wellbeingBandSpans(
  signal: WellbeingSignal,
  levels: WellbeingLevels,
): readonly WellbeingBandSpan[] {
  const { lowBelow, highAbove } = cutsFor(signal, levels);
  // TWO spans where the signal has no high cut, three where it does. A drawing paints exactly what
  // it is given, so a zero-height high stripe is not an acceptable stand-in for an absent one: it
  // would still put a colour change at the top of the box.
  if (highAbove === null) {
    return [
      { band: 'low', from: Number.NEGATIVE_INFINITY, to: lowBelow },
      { band: 'middling', from: lowBelow, to: Number.POSITIVE_INFINITY },
    ];
  }
  return [
    { band: 'low', from: Number.NEGATIVE_INFINITY, to: lowBelow },
    { band: 'middling', from: lowBelow, to: highAbove },
    { band: 'high', from: highAbove, to: Number.POSITIVE_INFINITY },
  ];
}

/**
 * Which band one reading is in.
 *
 * Every cut is STRICT, so sleep's OK band is `[6.5, ∞)` and mood's middling band is the closed
 * interval `[6, 8]`. That is the owner's wording read literally — "ponizej 6.5h", "ponizej 6.0
 * czerwona", "powyzej 8.0 zielona" — and it is the reading that leaves the cut itself out of the
 * verdict, which matters because a round figure is what a person is most likely to log.
 */
export function wellbeingBand(
  value: number,
  signal: WellbeingSignal,
  levels: WellbeingLevels,
): WellbeingBand {
  const { lowBelow, highAbove } = cutsFor(signal, levels);
  if (value < lowBelow) return 'low';
  if (highAbove !== null && value > highAbove) return 'high';
  return 'middling';
}
